import React, { useEffect, useState } from 'react';
import { RouteComponentProps } from 'react-router';

import { TeaserConsumer } from '@xingternal/braze';
import { importComponent } from '@xingternal/hops';
import { useExperiment } from '@xingternal/hub';

import { routes } from '@/config';
import {
  ErrorStatusBanner,
  FilterList,
  InternalSearchBox,
  QueryTags,
  Results,
  SearchAlertBanner,
  SearchAlertModal,
  VerifyEmailBanner,
} from '@/domain/search/components';
import { SearchAlertModalProvider, SearchAlertsProvider, SearchQueryProvider } from '@/domain/search/contexts';
import { useGlobalSearch, useRedirectLegacyURL } from '@/domain/search/hooks';
import { SerpsContainer, SerpsHeader, SerpsMetadata } from '@/domain/serps/components';
import { useSerpsMatch } from '@/domain/serps/hooks';
import { useBreakpoints, useLoginStates } from '@/hooks';

import { getVisionTypeParams } from './get-vision-type-params';
import { SearchBackButton } from './search-back-button';
import { SearchPageTracking } from './search-tracking';

import * as S from './search.styles';

const Navigation = importComponent(
  () => import('@xingternal/search-nav-tabs'),
  ({ Navigation }) => Navigation
);

export const SearchPage: React.FC<RouteComponentProps> = ({ location }) => {
  const { isMobile } = useBreakpoints();

  const { isLoggedOut } = useLoginStates();
  const { isGlobalSearch } = useGlobalSearch();
  const { isLegacyURL } = useRedirectLegacyURL({ redirect: false });
  const serps = useSerpsMatch();
  const isSerps = serps && isLoggedOut;
  const [trackAction, setTrackAction] = useState('');

  // ABACUS-421
  const isVisionTypeSearch = useExperiment('ABACUS-421') === 'B';

  useEffect(() => {
    if (!isLegacyURL && isVisionTypeSearch && !serps) {
      const newParams = getVisionTypeParams(location.search);
      window.location.replace(`${routes.visionTypeSearch}?${newParams}`);
    }
  }, [serps, isVisionTypeSearch, location.search, isLegacyURL]);

  return (
    <>
      <SearchQueryProvider>
        <SearchAlertsProvider>
          <SearchPageTracking trackAction={trackAction} setTrackAction={setTrackAction} />
          <S.PageContainer>
            {!isMobile && <SearchBackButton />}
            {isLoggedOut && !isSerps && (
              <S.BrazeContainer>
                <TeaserConsumer />
              </S.BrazeContainer>
            )}
            {isGlobalSearch && (
              <S.NavigationContainer>
                <Navigation />
              </S.NavigationContainer>
            )}
            {isSerps && (
              <>
                <SerpsMetadata serps={serps} />
                <SerpsHeader />
              </>
            )}
            <InternalSearchBox setTrackAction={setTrackAction} />
            {!isMobile && (
              <>
                <QueryTags />
                <FilterList />
              </>
            )}
            <VerifyEmailBanner />
            <SearchAlertModalProvider>
              <Results />
              <SearchAlertModal />
              {isSerps && <SerpsContainer />}
              <SearchAlertBanner />
            </SearchAlertModalProvider>
            <ErrorStatusBanner />
          </S.PageContainer>
        </SearchAlertsProvider>
      </SearchQueryProvider>
    </>
  );
};
